import type React from 'react';

/**
 * What the ⌘K palette searches, and how it orders what it finds (#603
 * follow-on 4). Kept apart from the component so the ranking can be
 * tested without a browser. Identical in both apps.
 */
export interface Command {
  id: string;
  label: string;
  /** Muted context after the label: the HOA a home is in, a member's email. */
  detail?: string;
  /** Words that should find this without being shown, e.g. "dark" for the theme toggle. */
  keywords?: string[];
  icon?: React.ReactNode;
  /** Short right-aligned note: "Page", "Action". */
  hint?: string;
  run: () => void;
}

export interface CommandGroup {
  key: string;
  label: string;
  items: Command[];
  /** How many to show before anything is typed; 0 keeps the group hidden until then. */
  idleLimit?: number;
}

/** Most results a group shows for a query — the palette is for jumping, not browsing. */
export const PER_GROUP = 6;

const words = (s: string) =>
  s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * Every term must match somewhere, or the item is out. The label counts
 * for more than the detail, and the start of a word for more than its
 * middle — so "oak" puts "12 Oak St" above "Soaking wet lawn".
 */
export const scoreCommand = (c: Command, terms: string[]): number => {
  const label = c.label.toLowerCase();
  const labelWords = words(c.label);
  const extra = [c.detail ?? '', ...(c.keywords ?? [])].join(' ').toLowerCase();
  const extraWords = words(extra);
  let total = 0;
  for (const t of terms) {
    if (label.startsWith(t)) total += 4;
    else if (labelWords.some((w) => w.startsWith(t))) total += 3;
    else if (label.includes(t)) total += 2;
    else if (extraWords.some((w) => w.startsWith(t))) total += 1.5;
    else if (extra.includes(t)) total += 1;
    else return 0;
  }
  return total;
};

/**
 * Empty query: each group's idle selection, in the order given. Otherwise
 * the best matches per group, and the groups themselves ordered by their
 * best match, so typing a name brings Members above Pages.
 */
export const rankCommands = (
  groups: CommandGroup[],
  query: string,
  perGroup = PER_GROUP
): CommandGroup[] => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) {
    return groups
      .map((g) => ({
        ...g,
        items: g.items.slice(0, g.idleLimit ?? g.items.length),
      }))
      .filter((g) => g.items.length > 0);
  }
  return groups
    .map((g, order) => {
      const scored = g.items
        .map((item, i) => ({ item, i, score: scoreCommand(item, terms) }))
        .filter((s) => s.score > 0)
        .sort((a, b) => b.score - a.score || a.i - b.i);
      return {
        group: { ...g, items: scored.slice(0, perGroup).map((s) => s.item) },
        best: scored[0]?.score ?? 0,
        order,
      };
    })
    .filter((r) => r.best > 0)
    .sort((a, b) => b.best - a.best || a.order - b.order)
    .map((r) => r.group);
};
